import type {ReactNode} from 'react'
import type {UITask} from '../../types/task'
import {resolveAgentProfileForTask} from '../../agents/resolve-agent-profile'
import {
  getLeadingVisualIconAttributes,
  getLeadingVisualStatusLabel,
} from '../../utils/get-leading-visual-icon-attributes'
import {getEffectivePrState} from '../../utils/task-artifact-helpers'
import {getReadableSessionState, SessionState} from '../../types/session'
import {getTaskTitle} from '../../utils/task-helpers'
import {TaskActions} from '../TaskActions'
import {generateTaskUrlForRepo} from '../../utils/generate-task-path'
import {isQuickChatTask} from '../../utils/is-quick-chat-task'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ownerAvatarPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-router'
import {useTaskAuthor} from '../../hooks/use-task-author'
import {SimpleListItem} from '@github-ui/simple-list-view/SimpleListItem'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {DotFillIcon, GitPullRequestIcon} from '@primer/octicons-react'
import {DiffStats} from '@github-ui/diffs/DiffStats'
import {SkeletonBox} from '@primer/react/experimental'
import {sendEvent} from '@github-ui/hydro-analytics'
import {clsx} from 'clsx'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {TaskTitle, type TaskTitleProps} from './TaskTitle'
import {TaskDescription, type TaskDescriptionProps} from './TaskDescription'
import {TaskRelativeTime} from './TaskRelativeTime'
import styles from './TaskListItem.module.css'

export type TaskTitleRenderProps = Pick<TaskTitleProps, 'title' | 'href' | 'onClick' | 'shimmer'>
export type TaskDescriptionRenderProps = Pick<TaskDescriptionProps, 'parts'>

export interface TaskListItemProps {
  task: UITask
  nwo?: string
  currentUserId?: number
  onTitleClick?: () => void
  isPRLoading?: boolean
  dataUpdatedAt?: number
  /** Omit the author when the surrounding page already identifies the automation. */
  hideAuthor?: boolean
  /** Custom description renderer. When omitted, renders default author + time description. */
  renderDescription?: (task: UITask, props: TaskDescriptionRenderProps) => ReactNode
  /** Custom title renderer. When omitted, renders TaskTitle with default text/URL/shimmer. */
  renderTitle?: (task: UITask, props: TaskTitleRenderProps) => ReactNode
  /** Full URL builder. */
  getTaskUrl?: (task: UITask, nwo: string | undefined, searchParams: URLSearchParams) => string | undefined
  /** Analytics event target for click tracking */
  analyticsTarget?: string
  /**
   * When true, suppresses the yellow left-bar styling applied to needs-attention items.
   * Use this when the session list already exclusively shows needs-attention sessions
   * (e.g. the Needs Attention tab), making the per-item highlight redundant.
   */
  suppressAttentionStyling?: boolean
}

function defaultGetTaskUrl(task: UITask, nwo: string | undefined, searchParams: URLSearchParams): string | undefined {
  return generateTaskUrlForRepo({
    repositoryNwo: nwo,
    taskId: task.id ?? undefined,
    sessionId: task.latest_session_id,
    searchParams,
  })
}

function defaultRenderTitle(task: UITask, props: TaskTitleRenderProps): ReactNode {
  return <TaskTitle task={task} {...props} />
}

export function TaskListItem({
  task,
  nwo,
  currentUserId,
  onTitleClick,
  isPRLoading,
  dataUpdatedAt,
  hideAuthor = false,
  renderDescription,
  renderTitle = defaultRenderTitle,
  getTaskUrl = defaultGetTaskUrl,
  analyticsTarget = 'TASK',
  suppressAttentionStyling = false,
}: TaskListItemProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {latest_session_state: state, archived_at} = task
  const title = getTaskTitle(task, task.pull?.title)
  const taskUrl = getTaskUrl(task, nwo, searchParams)

  const prState = getEffectivePrState(task.artifact_state, task.pull)
  const prInMergeQueue = task.pull?.inMergeQueue ?? false
  const agentProfile = resolveAgentProfileForTask(task)
  const [repoOwner, repoName] = nwo?.split('/') ?? []
  const currentUserIsOwner = currentUserId != null && task.creator_id === currentUserId
  const pullRequestPath = task.pull ? new URL(task.pull.url, 'https://github.com').pathname : undefined

  const taskAuthor = useTaskAuthor(task, repoOwner, repoName)
  const TrailingVisual = agentProfile.slots.TrailingVisual

  const leadingVisualAttributes = task.isOptimistic
    ? null
    : getLeadingVisualIconAttributes(prState, prInMergeQueue, state, !!archived_at, task.agentSlug)
  const LeadingVisualIcon = leadingVisualAttributes?.icon

  const handleTitleClick = (e: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (!taskUrl || e.metaKey || e.ctrlKey) {
      return
    }

    e.preventDefault()
    sendEvent('agent_sessions.task_list_item_click', {
      target: analyticsTarget,
      task_id: task.id,
    })
    onTitleClick?.()
    navigate(taskUrl)
  }

  const needsAttention =
    isFeatureEnabled('copilot_mission_control_needs_attention') && state === SessionState.WaitingForUser
  const needsAttentionLabel = needsAttention ? getReadableSessionState(state) : undefined
  const defaultLeadingVisualStatusLabel = getLeadingVisualStatusLabel(prState, prInMergeQueue, state, !!archived_at)
  const leadingVisualStatusLabel =
    needsAttentionLabel && defaultLeadingVisualStatusLabel === needsAttentionLabel
      ? undefined
      : defaultLeadingVisualStatusLabel

  const showInitializing = task.isOptimistic || (!task.creator_login && state === SessionState.Queued)

  const defaultParts: ReactNode[] = showInitializing
    ? [<span key="init">Initializing session...</span>]
    : [
        <span key="author">
          {!hideAuthor && <>{taskAuthor.displayName} &middot; </>}
          <TaskRelativeTime task={task} />
        </span>,
      ]

  if (needsAttentionLabel) {
    defaultParts.push(<span key="state">{needsAttentionLabel}</span>)
  }

  const defaultDescriptionProps: TaskDescriptionRenderProps = {
    parts: defaultParts,
  }

  const descriptionContent = renderDescription ? (
    renderDescription(task, defaultDescriptionProps)
  ) : (
    <TaskDescription {...defaultDescriptionProps} />
  )

  const showIconShimmer =
    !!isPRLoading && task.resource_id > -1 && !task.isOptimistic && !task.pull && !task.artifact_state

  const creatorAvatar = task.creator_login ? (
    <GitHubAvatar src={ownerAvatarPath({owner: task.creator_login})} size={20} alt={task.creator_login} />
  ) : null

  return (
    <SimpleListItem
      className={clsx(
        styles.item,
        task.isOptimistic && styles.optimisticItem,
        needsAttention && !suppressAttentionStyling && styles.attentionItem,
      )}
    >
      <SimpleListItem.LeadingVisual className={styles.itemLeadingVisual}>
        {showIconShimmer ? (
          <div className={styles.iconSkeletonWrapper}>
            <SkeletonBox height="16px" width="16px" className={styles.iconSkeleton} />
          </div>
        ) : task.isOptimistic ? (
          <div style={{color: 'var(--fgColor-attention)'}}>
            <DotFillIcon />
          </div>
        ) : LeadingVisualIcon && leadingVisualAttributes ? (
          <>
            <div
              style={{
                color: leadingVisualAttributes.color,
                marginTop: leadingVisualAttributes.marginTop ?? '1px',
              }}
            >
              <LeadingVisualIcon />
            </div>
            {leadingVisualStatusLabel && <span className="sr-only">{leadingVisualStatusLabel}</span>}
          </>
        ) : null}
      </SimpleListItem.LeadingVisual>
      {renderTitle(task, {title, href: taskUrl, onClick: handleTitleClick, shimmer: !!task.isOptimistic})}
      {descriptionContent}
      <SimpleListItem.Actions className={styles.itemActions}>
        <div className={styles.prNumberButtonContainer}>
          {!task.isOptimistic &&
            task.pull &&
            (pullRequestPath ? (
              <Link
                className={styles.prNumberButton}
                to={pullRequestPath}
                aria-label={`View pull request #${task.pull.number}`}
              >
                <GitPullRequestIcon />#{task.pull.number}
              </Link>
            ) : (
              <a
                className={styles.prNumberButton}
                href={task.pull.url}
                aria-label={`View pull request #${task.pull.number}`}
              >
                <GitPullRequestIcon />#{task.pull.number}
              </a>
            ))}
        </div>
        <div className={styles.diffStatsContainer}>
          {state !== 'in_progress' && task.pull?.pr_diff_summary && (
            <DiffStats
              linesAdded={task.pull.pr_diff_summary.lines_added}
              linesChanged={task.pull.pr_diff_summary.lines_changed}
              linesDeleted={task.pull.pr_diff_summary.lines_deleted}
              hideSquares
              className={styles.diffStats}
            />
          )}
        </div>
        <div className={styles.trailingIcons}>
          <div className={styles.typeIcon}>
            {TrailingVisual && (
              <TrailingVisual
                state={task.latest_session_state}
                staleAt={task.stale_at}
                dataUpdatedAt={dataUpdatedAt}
                sharingStatus={task.sharing_status}
              />
            )}
          </div>
        </div>
        <div className={styles.creatorAvatarColumn}>{creatorAvatar}</div>
      </SimpleListItem.Actions>
      {!task.isOptimistic && (
        <SimpleListItem.TrailingActions className={styles.trailingActions}>
          <TaskActions
            ariaLabel={`Actions for task: ${title}`}
            state={state}
            taskId={task.id}
            pull={task.pull}
            repoOwner={repoOwner}
            repoName={repoName}
            currentUserIsOwner={currentUserIsOwner}
            archivedAt={archived_at}
            eventType={task.event_type}
            eventIdentifiers={task.event_identifiers}
            interactiveSession={agentProfile.interactiveSession}
            agentSlug={agentProfile.slug}
            isQuickChatTask={isQuickChatTask(task)}
            hidePull
            hideCopy={agentProfile.privateSessions}
            hideShare={!agentProfile.privateSessions}
            sharingStatus={task.sharing_status}
            automationId={task.automation_id}
          />
        </SimpleListItem.TrailingActions>
      )}
    </SimpleListItem>
  )
}
